import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { blogPosts } from '../data/mock';

const Blog = () => {
  return (
    <>
      <Helmet>
        <title>{`Haridwar Travel Blog | Hotel Suvidha Deluxe`}</title>
        <meta name="description" content="Read travel guides, sightseeing tips, and pilgrimage information for Haridwar." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1585083783155-76ae62b2914e?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-navy-900">Travel Blog</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            Guides, tips, and stories to help you plan your perfect Haridwar visit
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="block"
              aria-label={`Read: ${post.title}`}
            >
              <Card className="border-0 shadow-xl overflow-hidden group hover:shadow-2xl transition-shadow">
                <div className="relative h-56 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  loading="lazy"
                  width={400}
                  height={224}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {post.readTime || '5 min read'}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3 group-hover:text-gold-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                  <Button asChild variant="link" className="text-gold-600 hover:text-gold-700 p-0">
                    <span>
                      Read More
                      <ArrowRight className="ml-2" size={16} />
                    </span>
                  </Button>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Placeholder for More Posts */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-6">More travel guides and tips coming soon!</p>
          <Link to="/contact">
            <Button variant="outline" className="border-gold-600 text-gold-600 hover:bg-gold-600 hover:text-white">
              Subscribe to Updates
            </Button>
          </Link>
        </div>
      </section>

      {/* Featured Topics */}
      <section className="py-20 mt-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Popular Topics</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our most popular travel guides
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {[
              'Temple Visits',
              'Ganga Aarti',
              'Kumbh Mela',
              'Adventure Sports',
              'Local Cuisine',
              'Yoga & Meditation',
              'Wildlife Safari',
              'Photography Spots',
            ].map((topic, idx) => (
              <span
                key={idx}
                className="px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg hover:bg-gold-50 hover:text-gold-700 text-gray-700 font-medium transition-all cursor-pointer"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 font-serif text-navy-900">Plan Your Haridwar Trip</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let us help you create unforgettable memories in the holy city
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-10 py-6 text-lg">
              Book Your Stay
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Blog;
